import { z } from "zod";
import type { Address, Hex } from "viem";
import { merkleV4TradeSchema, v4PoolKeySchema } from "./merkle-v4-schema";

export const addressSchema = z.string().regex(/^0x[0-9a-fA-F]{40}$/).transform((value) => value as Address);
export const hex32Schema = z.string().regex(/^0x[0-9a-fA-F]{64}$/).transform((value) => value as Hex);
export const unsignedDecimalSchema = z.string().regex(/^(0|[1-9][0-9]*)$/);
export const uint256Schema = unsignedDecimalSchema.max(78).refine((value) =>
  /^(0|[1-9][0-9]{0,77})$/.test(value) && BigInt(value) < (1n << 256n), "invalid uint256");
const bytesSchema = z.string().regex(/^0x(?:[0-9a-fA-F]{2})+$/).transform((value) => value as Hex);
export const mintAuthorizationSchema = z.object({
  version: z.literal(3),
  chainId: z.number().int().positive().safe(),
  verifyingContract: addressSchema,
  signerAddress: addressSchema,
  message: z.object({
    authorizationId: hex32Schema, instanceId: hex32Schema, wallet: addressSchema, sourceToken: addressSchema,
    proofKey: hex32Schema, challengeCommitment: hex32Schema, selectedTierUsd: uint256Schema,
    rewardPolicyVersion: uint256Schema, rewardPolicyHash: hex32Schema, eligibilitySnapshotHash: hex32Schema,
    coverageRevision: uint256Schema, asOfBlock: uint256Schema, asOfBlockHash: hex32Schema,
    priceTimestamp: uint256Schema, quotedMintFeeWei: uint256Schema, feeStateHash: hex32Schema,
    userContribution: hex32Schema, issuedAt: uint256Schema, deadline: uint256Schema,
  }),
  signature: bytesSchema,
});
export const nullableUnsignedDecimalSchema = unsignedDecimalSchema.nullable();
export const tierSchema = z.union([z.literal(10), z.literal(100), z.literal(500), z.literal(1_000), z.literal(2_000), z.literal(5_000)]);
export const nftTierSchema = z.union([z.literal(0), z.literal(50), tierSchema]);

export const tokenSchema = z.object({
  venue: z.literal("MERKLE_V4").optional(), decimals: z.number().int().min(0).max(255).optional(),
  poolManager: addressSchema.nullable().optional(), poolId: hex32Schema.nullable().optional(),
  poolKey: v4PoolKeySchema.nullable().optional(),
  chainId: z.number().int().positive(),
  tokenAddress: addressSchema,
  creator: addressSchema,
  name: z.string(),
  symbol: z.string(),
  curveAddress: addressSchema.nullable(),
  poolAddress: addressSchema.nullable(),
  pairTokenAddress: addressSchema,
  launchTxHash: z.string(),
  launchBlock: z.number().int().nonnegative(),
  tracking: z.boolean(),
  createdAt: z.string(),
});

export const mintDisplayCacheSchema = z.object({
  storage: z.literal("SQLITE"),
  ttlSeconds: z.number().int().positive(),
  updatedAt: z.iso.datetime().nullable(),
  expiresAt: z.iso.datetime().nullable(),
  stale: z.boolean(),
  errorCode: z.string().nullable(),
});

const mintPreparationFields = {
  providerCheckMode: z.string().optional(),
  preparationAvailable: z.boolean().optional(),
  cache: mintDisplayCacheSchema.optional(),
};

export const tierMintFeesSchema = z.object({
  "10": uint256Schema, "100": uint256Schema, "500": uint256Schema,
  "1000": uint256Schema, "2000": uint256Schema, "5000": uint256Schema,
});
export const rewardRateSchema = z.number().int().nonnegative().safe();
export const rewardDenominatorSchema = z.union([z.literal(10_000), z.literal(1_000_000)]);

const mintStateSchema = z.object({
  ...mintPreparationFields,
  available: z.boolean(),
  contractAddress: addressSchema.nullable(),
  cumulativeTotalMinted: uint256Schema.nullable(),
  powTotalMinted: uint256Schema.nullable().optional(),
  powTotalMintedAsOfBlock: z.number().int().nonnegative().safe().nullable().optional(),
  mintFeeWei: uint256Schema.nullable(),
  tierMintFeesWei: tierMintFeesSchema.optional(),
  nextStepTierMintFeesWei: tierMintFeesSchema.optional(),
  mintFeeStepSize: z.number().int().positive().safe().optional(),
  successfulMintsUntilNextFeeStep: uint256Schema.optional(),
  mintEnabled: z.boolean(),
  blockNumber: z.number().int().nonnegative().optional(),
  blockHash: hex32Schema.optional(),
  reason: z.string().optional(),
  testOnly: z.boolean().optional(),
  source: z.string().optional(),
  gatewayMode: z.string().optional(),
  rewardPolicyVersion: unsignedDecimalSchema.optional(),
  rewardPolicyHash: hex32Schema.optional(),
  randomnessAdapter: addressSchema.optional(),
  randomnessProviderAvailable: z.boolean().optional(),
  randomnessAdapterTestOnly: z.boolean().nullable().optional(),
}).passthrough();

export const bootstrapSchema = z.object({
  trade: merkleV4TradeSchema.optional(),
  environment: z.enum(["development", "test", "production"]),
  forkTestOnly: z.boolean(),
  sourceChainId: z.number().int().positive(),
  siwe: z.object({ chainId: z.number().int().positive(), domain: z.string().min(1), uri: z.string().url() }),
  sourceTokenSelection: z.enum(["REGISTERED_ACTIVE_ONLY", "EXPLICIT_PROJECT_SOURCE_ONLY"]),
  tokens: z.array(tokenSchema),
  capabilities: z.object({
    miningEnabled: z.boolean(),
    publicMintEnabled: z.boolean(),
    mainnetWritesEnabled: z.boolean(),
    mintGatewayMode: z.enum(["DISABLED", "MOCK_TEST_ONLY", "LOCAL_EVM_TEST_ONLY", "MAINNET_READ_ONLY"]),
    localTestOnlyMintTransactionEnabled: z.boolean(),
    randomnessRuntimeMode: z.string(),
    randomnessProviderApproved: z.boolean(),
    fallbackRandomnessAllowed: z.literal(false),
    randomnessRerollAllowed: z.literal(false),
  }),
  contracts: z.object({
    poolManagerAddress: addressSchema.nullable().optional(), universalRouterAddress: addressSchema.nullable().optional(),
    feeHookAddress: addressSchema.nullable().optional(), permit2Address: addressSchema.nullable().optional(),
    stateViewAddress: addressSchema.nullable().optional(), positionManagerAddress: addressSchema.nullable().optional(),
    lpLockAddress: addressSchema.nullable().optional(), metadataRendererAddress: addressSchema.nullable().optional(),
    readinessAddress: addressSchema.nullable().optional(), quoterAddress: addressSchema.nullable().optional(),
    mintChainId: z.number().int().positive().nullable(),
    collectionAddress: addressSchema.nullable(),
    rewardVaultAddress: addressSchema.nullable(),
    dedicatedFeeReceiverAddress: addressSchema.nullable(),
    randomnessAdapterAddress: addressSchema.nullable(),
    randomnessCoordinatorAddress: addressSchema.nullable(),
    authorizationSignerAddress: addressSchema.nullable(),
  }),
  deployment: z.object({
    venue: z.literal("MERKLE_V4").optional(), instanceId: hex32Schema.optional(),
    sourceTokenStatus: z.enum(["WAITING_FOR_LAUNCH", "REGISTERED"]),
    projectContractsStatus: z.enum([
      "NOT_DEPLOYED", "NOT_CONFIGURED", "CONFIGURED_UNVERIFIED_READ_ONLY", "VERIFIED_READ_ONLY", "VERIFIED_WRITE_ENABLED", "LOCAL_TEST_ONLY",
    ]),
    sourceChainId: z.number().int().positive(),
    executionChainId: z.number().int().positive().nullable(),
    writesEnabled: z.boolean(),
  }),
  mintState: mintStateSchema,
});

export const tierDisplaySchema = z.object({
  status: z.enum(["CURRENT", "LAST_CONFIRMED_SNAPSHOT", "UNAVAILABLE"]),
  eligibleTiers: z.array(tierSchema).nullable(),
  maxTierUsd: tierSchema.nullable(),
  asOfBlock: z.number().int().nonnegative().safe().nullable(),
  asOfBlockHash: hex32Schema.nullable(),
  authorization: z.literal(false),
  updating: z.boolean(),
  source: z.enum(["CONFIRMED_DB_SNAPSHOT", "NATIVE_V3_POOL_ONCHAIN", "MERKLE_V4_POOL_EVENTS"]).nullable().optional(),
  lagBlocks: z.number().int().nonnegative().safe().nullable().optional(),
});

export const netBuySchema = z.object({
  nativeSymbol: z.literal("HYPE").optional(),
  priceAsset: z.literal("HYPE").optional(),
  netBuyHype: z.string().regex(/^(0|[1-9][0-9]*)(\.[0-9]+)?$/).optional(),
  nativeUsd: z.string().regex(/^(0|[1-9][0-9]*)(\.[0-9]+)?$/).nullable().optional(),
  poolAddress: addressSchema.nullable().optional(),
  poolManager: addressSchema.optional(),
  poolId: hex32Schema.optional(),
  authorization: z.literal(false).optional(),
  tierDisplay: tierDisplaySchema.optional(),
  netBuyThresholds: z.array(z.object({
    tierUsd: tierSchema,
    minNetBuyUsd: z.union([z.number().finite().nonnegative(),
      z.string().regex(/^(0|[1-9][0-9]*)(\.[0-9]+)?$/)]).transform(String),
  })).optional(),
  chainId: z.number().int().positive(),
  tokenAddress: addressSchema,
  wallet: addressSchema,
  netBuyWei: unsignedDecimalSchema,
  netBuyEth: z.string(),
  netBuyScope: z.enum(["PARTIAL_UNVERIFIED", "CONFIRMED_SNAPSHOT"]),
  netBuyUsd: z.string().nullable(),
  eligibilityAvailable: z.boolean(),
  eligibleTiers: z.array(tierSchema).nullable(),
  maxTierUsd: tierSchema.nullable(),
  blockingReasons: z.array(z.string()),
  asOfBlock: z.number().int().nonnegative().nullable(),
  asOfBlockHash: hex32Schema.nullable(),
  priceSource: z.string().nullable(),
  ethUsdTimestamp: z.number().int().nonnegative().nullable(),
}).passthrough().superRefine((value, ctx) => {
  if (value.chainId !== 99901) return;
  const v4 = value.source === "MERKLE_V4_POOL_EVENTS";
  const poolIdentity = v4 ? value.poolAddress === null && Boolean(value.poolManager && value.poolId) &&
    value.tierDisplay?.source === "MERKLE_V4_POOL_EVENTS" : Boolean(value.poolAddress) &&
    value.tierDisplay?.source === "NATIVE_V3_POOL_ONCHAIN";
  if (value.nativeSymbol !== "HYPE" || value.priceAsset !== "HYPE" || value.netBuyHype === undefined ||
    value.nativeUsd === undefined || !poolIdentity || value.authorization !== false ||
    value.asOfBlock === null || value.asOfBlockHash === null)
    ctx.addIssue({ code: "custom", message: "Incomplete Elysium net-buy snapshot identity" });
});

export const rewardPoolSchema = z.object({
  balanceMode: z.string().optional(),
  settledRewardWei: nullableUnsignedDecimalSchema.optional(),
  chainId: z.number().int().positive(),
  collectionAddress: addressSchema.nullable(),
  vaultAddress: addressSchema.nullable(),
  sourceToken: addressSchema,
  poolAvailable: z.boolean(),
  dataStatus: z.string(),
  blockingReasons: z.array(z.string()),
  availableRewardWei: nullableUnsignedDecimalSchema,
  totalDepositedWei: nullableUnsignedDecimalSchema,
  totalPaidWei: nullableUnsignedDecimalSchema,
  asOfBlock: z.number().int().nonnegative().nullable(),
  asOfBlockHash: hex32Schema.nullable(),
}).passthrough();

export const miningDifficultySchema = z.object({
  instanceId: hex32Schema,
  epochId: z.number().int().nonnegative().safe(),
  difficultyBits: z.string().regex(/^(0|[1-9][0-9]*)(\.[0-9]+)?$/)
    .refine(value => Number(value) <= 256, "invalid difficulty bits"),
  targetExclusive: uint256Schema.refine(value => BigInt(value) > 0n, "invalid target"),
});

export const challengeSchema = z.object({
  algorithm: z.literal("KECCAK256_ABI_V1"),
  protocolVersion: z.literal(1),
  instanceId: hex32Schema,
  chainId: z.number().int().positive(),
  tokenAddress: addressSchema,
  walletAddress: addressSchema,
  challengeId: hex32Schema,
  seed: hex32Schema,
  difficultyEpochId: z.number().int().nonnegative(),
  targetExclusive: unsignedDecimalSchema,
  issuedAt: z.number().int().nonnegative(),
  expiresAt: z.number().int().nonnegative(),
  challengeCommitment: hex32Schema,
  status: z.enum(["ACTIVE", "SOLVED", "EXPIRED", "CANCELLED"]),
});

export const proofSchema = z.object({
  tierDisplay: tierDisplaySchema.optional(),
  preparationCheckAvailable: z.boolean().optional(),
  proofId: hex32Schema,
  challengeId: hex32Schema,
  proofStatus: z.enum(["VERIFIED", "EXPIRED", "CONSUMED"]),
  nonce: unsignedDecimalSchema,
  digest: hex32Schema,
  difficultyEpochId: z.number().int().nonnegative(),
  targetExclusive: unsignedDecimalSchema,
  acceptedAt: z.number().int().nonnegative(),
  expiresAt: z.number().int().nonnegative().safe(),
  eligibilityAvailable: z.boolean().optional(),
  eligibleTiers: z.array(tierSchema).nullable().optional(),
  maxTierUsd: tierSchema.nullable().optional(),
  blockingReasons: z.array(z.string()).optional(),
  asOfBlock: z.number().int().nonnegative().nullable().optional(),
  asOfBlockHash: hex32Schema.nullable().optional(),
  coverageRevision: z.number().int().nonnegative().nullable().optional(),
  priceSource: z.string().nullable().optional(),
  ethUsdTimestamp: z.number().int().nonnegative().nullable().optional(),
}).passthrough();

export const proofEligibilitySchema = proofSchema.extend({
  eligibilityAvailable: z.boolean(),
  eligibleTiers: z.array(tierSchema).nullable(),
  blockingReasons: z.array(z.string()),
});

export const mintFeeSchema = mintStateSchema;

export const preparationSchema = z.object({
  eligibilitySnapshot: z.object({ netBuyWei: uint256Schema }).passthrough().optional(),
  preparationId: hex32Schema,
  authorizationId: hex32Schema,
  proofKey: hex32Schema,
  sourceChainId: z.number().int().positive().safe(),
  authorization: mintAuthorizationSchema.nullable(),
  powProofId: hex32Schema,
  chainId: z.number().int().positive(),
  wallet: addressSchema,
  sourceToken: addressSchema,
  selectedTierUsd: tierSchema,
  feeQuoteWei: unsignedDecimalSchema,
  deadline: z.number().int().nonnegative().safe(),
  contractAddress: addressSchema.nullable(),
  calldata: z.string().regex(/^0x(?:[0-9a-fA-F]{2})*$/).nullable(),
  call: z.object({
    mode: z.string(),
    sendable: z.boolean(),
    contractAddress: addressSchema.nullable(),
    calldata: z.string().regex(/^0x(?:[0-9a-fA-F]{2})*$/).nullable(),
    valueWei: nullableUnsignedDecimalSchema,
    reason: z.string().nullable(),
  }),
  // The current BE E2E response can carry true. Parsing is not send permission.
  mintEnabled: z.boolean(),
  testOnly: z.boolean(),
}).passthrough();

const canonicalMediaIdentitySchema = z.object({
  chainId: z.number().int().positive(),
  collectionAddress: addressSchema,
  tokenId: unsignedDecimalSchema,
  snapshot: z.object({
    blockNumber: z.number().int().nonnegative(),
    blockHash: hex32Schema,
  }).nullable(),
});

const canonicalMediaSuccessSchema = canonicalMediaIdentitySchema.extend({
  available: z.literal(true),
  dataStatus: z.literal("CURRENT"),
  blockingReasons: z.array(z.string()).length(0),
  source: z.enum(["MOCK", "LOCAL_EVM", "MAINNET_EVM"]),
  tokenUri: z.string().min(1).max(1_500_000),
  tokenUriHash: hex32Schema,
  metadata: z.object({
    name: z.string().min(1).max(256),
    description: z.string().min(1).max(2_048),
    rewardStatus: z.enum(["PENDING_RANDOMNESS", "RANDOMNESS_READY", "FINALIZED"]),
    rewardBps: z.number().int().min(0).max(10_000).nullable(),
    rewardPpm: rewardRateSchema.max(20_000).nullable().optional(),
    rewardDenominator: rewardDenominatorSchema.optional(),
  }),
  image: z.union([z.object({
    url: z.string().min(1).max(1_500_000),
    mediaType: z.literal("image/svg+xml"),
    encoding: z.enum(["base64", "utf8"]),
  }), z.object({
    url: z.string().min(1).max(2_048),
    mediaType: z.literal("image/png"),
    encoding: z.literal("url"),
  })]),
});

const canonicalMediaFailureSchema = canonicalMediaIdentitySchema.extend({
  available: z.literal(false),
  dataStatus: z.enum(["UNAVAILABLE", "INVALID"]),
  blockingReasons: z.array(z.string()).min(1),
  source: z.enum(["MOCK", "LOCAL_EVM", "MAINNET_EVM"]).nullable(),
  tokenUri: z.null(),
  tokenUriHash: z.null(),
  metadata: z.null(),
  image: z.null(),
});

export const canonicalMediaSchema = z.discriminatedUnion("available", [
  canonicalMediaSuccessSchema,
  canonicalMediaFailureSchema,
]);

export const nftSchema = z.object({
  chainId: z.number().int().positive(),
  collectionAddress: addressSchema,
  tokenId: uint256Schema,
  mintFeeWei: uint256Schema.nullable().optional(),
  owner: addressSchema.nullable(),
  sourceToken: addressSchema.nullable(),
  selectedTierUsd: nftTierSchema.nullable(),
  status: z.enum(["PENDING_RANDOMNESS", "RANDOMNESS_READY", "FINALIZED", "BURNED", "UNKNOWN"]),
  dataStatus: z.string(),
  rewardPolicyVersion: nullableUnsignedDecimalSchema,
  rewardPolicyHash: hex32Schema.nullable(),
  bandIndex: z.number().int().nullable(),
  rewardBps: z.number().int().nullable(),
  rewardPpm: rewardRateSchema.nullable().optional(),
  rewardRate: rewardRateSchema.nullable().optional(),
  rewardDenominator: rewardDenominatorSchema.nullable().optional(),
  canonicalMedia: canonicalMediaSchema,
  transactions: z.object({
    mint: z.string().nullable(),
    randomnessRequested: z.string().nullable(),
    randomnessReady: z.string().nullable(),
    rewardFinalized: z.string().nullable(),
    redeemed: z.string().nullable(),
  }),
  snapshot: z.object({ blockNumber: z.number().int().nullable(), blockHash: hex32Schema.nullable() }),
}).passthrough();

export const inventorySchema = z.object({
  chainId: z.number().int().positive(),
  collectionAddress: addressSchema.nullable(),
  owner: addressSchema,
  inventoryAvailable: z.boolean(),
  dataStatus: z.string(),
  blockingReasons: z.array(z.string()),
  snapshot: z.object({ blockNumber: z.number().int().nonnegative(), blockHash: hex32Schema }).nullable(),
  items: z.array(nftSchema).nullable(),
  nextCursor: z.string().nullable(),
}).passthrough();

export const redeemPreviewSchema = z.object({
  available: z.boolean(),
  sourceToken: addressSchema.nullable().optional(),
  rewardBps: z.number().int().nullable().optional(),
  rewardRate: rewardRateSchema.nullable().optional(),
  rewardDenominator: rewardDenominatorSchema.nullable().optional(),
  poolBalanceWei: nullableUnsignedDecimalSchema.optional(),
  expectedPayoutWei: nullableUnsignedDecimalSchema.optional(),
  redeemable: z.boolean().optional(),
  reason: z.string().nullable().optional(),
  estimateOnly: z.boolean().optional(),
}).passthrough();

const policyRangeSchema = z.object({ min: rewardRateSchema, max: rewardRateSchema });
const policyBandsSchema = z.array(z.object({ min: rewardRateSchema, max: rewardRateSchema,
  weightBps: rewardRateSchema.max(10_000) }));
const rewardPolicyCommonSchema = z.object({
  policyHash: hex32Schema,
  weightsBps: z.array(z.number().int().nonnegative()).length(5),
  weightTotalBps: z.number().int().positive(),
  bandCount: z.number().int().positive(),
  distributionStatus: z.literal("DISTRIBUTION_APPROVED"),
  providerStatus: z.enum(["PROVIDER_PENDING", "PROVIDER_ONCHAIN_VERIFIED"]),
  mintEnabled: z.boolean(),
  testOnly: z.boolean(),
}).passthrough();

export const rewardPolicySchema = z.discriminatedUnion("version", [
  rewardPolicyCommonSchema.extend({
    version: z.literal("1"), rewardDenominator: z.literal(10_000).optional(),
    rewardIncrementBps: z.number().int().positive().safe(),
    tiers: z.array(z.object({ tierUsd: tierSchema, rangeBps: policyRangeSchema,
      bandStepBps: rewardRateSchema, bands: policyBandsSchema })),
  }),
  rewardPolicyCommonSchema.extend({
    version: z.literal("2"), rewardDenominator: z.literal(1_000_000),
    rewardIncrementBps: z.null().optional(), rewardIncrementPpm: z.number().int().positive().safe(),
    tiers: z.array(z.object({ tierUsd: tierSchema, rangePpm: policyRangeSchema,
      bandStepPpm: rewardRateSchema, bands: policyBandsSchema,
      baseMintFeeWei: uint256Schema, currentMintFeeWei: uint256Schema.nullable() })),
  }),
]);

export const healthSchema = z.object({ status: z.string() }).passthrough();

const mirrorFreshnessSchema = z.object({
  status: z.string(), current: z.boolean(), reasons: z.array(z.string()),
  lagBlocks: z.number().int().nonnegative().safe().nullable().optional(),
}).passthrough();

// These are observation DTOs, not execution permissions.
export const randomnessHealthSchema = z.object({
  ...mintPreparationFields,
  status: z.string(), providerApproval: z.string(), mintEnabled: z.boolean(),
  fallbackAllowed: z.literal(false), rerollAllowed: z.literal(false),
  providerReadiness: z.object({ status: z.string(), blockingReasons: z.array(z.string()) }).passthrough().nullable(),
  mirror: z.object({ freshness: mirrorFreshnessSchema }).passthrough(),
}).passthrough();

export const nftRandomnessSchema = z.object({
  tokenId: unsignedDecimalSchema, status: z.string(), onChainStatus: nftSchema.shape.status,
  stalled: z.boolean(), stallReason: z.string().nullable(),
  mirrorFreshness: mirrorFreshnessSchema,
  snapshot: z.object({ blockNumber: z.number().int().nonnegative().safe().nullable(), blockHash: hex32Schema.nullable() }),
  recovery: z.object({ fallbackAllowed: z.literal(false), rerollAllowed: z.literal(false) }),
}).passthrough();

export type BootstrapResponse = z.infer<typeof bootstrapSchema>;
export type NetBuyResponse = z.infer<typeof netBuySchema>;
export type ChallengeResponse = z.infer<typeof challengeSchema>;
export type ProofResponse = z.infer<typeof proofSchema>;
export type ProofEligibilityResponse = z.infer<typeof proofEligibilitySchema>;
export type InventoryResponse = z.infer<typeof inventorySchema>;
